<script lang="ts">
  import { PUBLIC_GOOGLE_MAPS_API_KEY } from '$env/static/public'
  import {
    Button,
    Skeleton,
    Tooltip,
    clickOutside,
    type Language,
  } from '@bruce/components'
  import { Loader } from '@googlemaps/js-api-loader'
  import { utils } from '$lib/utils'
  import { STRINGS } from '$lib/constants/strings'
  import { page } from '$app/stores'
  import { goto } from '$app/navigation'

  export let imageUrl: string
  export let showInfo = false
  export let studio: {
    id: number
    name: string
    photo: string | null
  }

  let pinElement: HTMLElement | undefined = undefined
  let imgLoading = true
  $: lang = $page.params.lang as Language
  $: localeRoute = utils.getRoute(lang)

  async function createPin(pinElement: HTMLElement) {
    const loader = new Loader({
      apiKey: PUBLIC_GOOGLE_MAPS_API_KEY,
      version: 'weekly',
    })

    const { PinElement } = await loader.importLibrary('marker')

    const pin = new PinElement({
      background: '#000',
      borderColor: '#000',
      glyphColor: '#fff',
    })

    pinElement.appendChild(pin.element)

    pinElement.addEventListener('click', () => {
      showInfo = true
    })

    pinElement.addEventListener('touchstart', () => {
      showInfo = true
    })
  }

  $: if (pinElement) {
    createPin(pinElement)
  }
</script>

<div
  class="container"
  class:showInfo
  use:clickOutside
  on:click-outside={() => (showInfo = false)}
>
  <div bind:this={pinElement}></div>
  <Tooltip activator={pinElement} visible={showInfo} placement="top">
    <div class="studio-info">
      <img
        src={`${imageUrl}/fl_progressive,w_568,h_355,c_fill,b_black,o_70/${studio.photo}`}
        alt={studio.name}
        on:load={() => (imgLoading = false)}
        class:imgLoading
        loading="lazy"
      />
      <Skeleton visible={imgLoading} />
      <div>
        <h3>{studio.name}</h3>
        <Button
          href={localeRoute(
            `/studios/${studio.id}/${utils.toSpeakingUrl(studio.name)}`,
          )}
          target="_blank"
          variant="flat"
          on:touchstart={() =>
            goto(
              localeRoute(
                `/studios/${studio.id}/${utils.toSpeakingUrl(studio.name)}`,
              ),
            )}
        >
          {STRINGS[lang].STUDIO_MARKER_VIEW_STUDIO}
        </Button>
      </div>
    </div>
  </Tooltip>
</div>

<style lang="scss">
  .container {
    position: relative;

    &:hover:not(.showInfo) {
      cursor: pointer;
    }

    &:hover,
    &.showInfo {
      :global(.RIFJN-maps-pin-view-border),
      :global(.RIFvHW-maps-pin-view-background) {
        fill: var(--color-primary);
      }

      :global(.KWCFZI-maps-pin-view-default-glyph) {
        fill: var(--color-on-primary);
      }
    }

    .studio-info {
      display: flex;
      gap: 1em;

      img {
        width: 100px;
        border-radius: 0.5em;
        object-fit: cover;
      }

      div {
        display: flex;
        flex-direction: column;
        gap: 1em;

        h3 {
          margin: 0;
        }
      }
    }
  }

  :global(.GMAMP-maps-pin-view) {
    :global(.RIFJN-maps-pin-view-border),
    :global(.RIFvHW-maps-pin-view-background),
    :global(.KWCFZI-maps-pin-view-default-glyph) {
      transition: fill 0.2s ease-in-out;
    }
  }

  .container:hover {
    cursor: pointer;
  }
</style>
